import { useState, useRef } from 'react'
import { DEFAULT_CATEGORIES } from '../utils/gridHelpers.js'

export default function ShoppingList({
  activeList,
  onAddItem,
  onRemoveItem,
  onToggleItem,
  onUpdateItemCategory,
  onCreateList,
  onComputeRoute,
  calculating,
}) {
  const [inputValue, setInputValue] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('overig')
  const inputRef = useRef(null)

  function handleSubmit(e) {
    e.preventDefault()
    if (!inputValue.trim()) return

    // Auto-create list if none exists
    if (!activeList) {
      onCreateList().then(() => {
        onAddItem(inputValue.trim(), selectedCategory)
        setInputValue('')
        inputRef.current?.focus()
      })
      return
    }

    onAddItem(inputValue.trim(), selectedCategory)
    setInputValue('')
    inputRef.current?.focus()
  }

  const items = activeList?.items || []
  const uncheckedCount = items.filter(i => !i.checked).length
  const checkedCount = items.filter(i => i.checked).length

  return (
    <div className="shopping-list">
      <div className="panel">
        <div className="panel__header">
          <span className="panel__title">🛒 {activeList?.name || 'Boodschappenlijst'}</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {items.length} items
          </span>
        </div>
        <div className="panel__body">
          {/* Add item form */}
          <form onSubmit={handleSubmit} className="shopping-list__input-row">
            <input
              ref={inputRef}
              className="shopping-list__input"
              type="text"
              placeholder="Voeg item toe…"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
            />
            <select
              className="shopping-list__category-select"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              {DEFAULT_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <button type="submit" className="btn btn--primary" disabled={!inputValue.trim()}>
              Toevoegen
            </button>
          </form>

          {/* Items list */}
          <div className="shopping-list__items">
            {items.length === 0 && (
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                textAlign: 'center',
                padding: 'var(--space-6) 0',
              }}>
                Voeg items toe aan je boodschappenlijst
              </p>
            )}

            {/* Unchecked items first */}
            {items.filter(i => !i.checked).map(item => (
              <ShoppingItem
                key={item.id}
                item={item}
                onToggle={() => onToggleItem(item.id)}
                onRemove={() => onRemoveItem(item.id)}
                onCategoryChange={(cat) => onUpdateItemCategory(item.id, cat)}
              />
            ))}

            {/* Checked items */}
            {checkedCount > 0 && (
              <>
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  padding: 'var(--space-2) 0',
                  borderTop: '1px solid var(--color-border)',
                  marginTop: 'var(--space-2)',
                }}>
                  ✓ Verzameld ({checkedCount})
                </div>
                {items.filter(i => i.checked).map(item => (
                  <ShoppingItem
                    key={item.id}
                    item={item}
                    onToggle={() => onToggleItem(item.id)}
                    onRemove={() => onRemoveItem(item.id)}
                    onCategoryChange={(cat) => onUpdateItemCategory(item.id, cat)}
                  />
                ))}
              </>
            )}
          </div>

          {/* Route calculation area */}
          {items.length > 0 && (
            <div className="shopping-list__route-area">
              <div className="shopping-list__stats">
                <span><strong>{uncheckedCount}</strong> te pakken</span>
                <span><strong>{checkedCount}</strong> verzameld</span>
              </div>
              <button
                className="btn btn--success btn--lg btn--full"
                onClick={onComputeRoute}
                disabled={uncheckedCount === 0 || calculating}
              >
                {calculating ? (
                  <>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Route berekenen…
                  </>
                ) : (
                  '🗺️ Bereken optimale route'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ShoppingItem({ item, onToggle, onRemove, onCategoryChange }) {
  const cat = DEFAULT_CATEGORIES.find(c => c.id === item.category)

  return (
    <div className={`shopping-item ${item.checked ? 'shopping-item--checked' : ''}`}>
      <input
        type="checkbox"
        className="shopping-item__checkbox"
        checked={item.checked}
        onChange={onToggle}
      />
      <span className="shopping-item__name">{item.name}</span>
      {cat && (
        <span
          className="shopping-item__category"
          style={{
            backgroundColor: cat.color + '1A',
            color: cat.color,
          }}
        >
          {cat.name}
        </span>
      )}
      <button
        className="shopping-item__remove"
        onClick={onRemove}
        title="Verwijderen"
      >
        ✕
      </button>
    </div>
  )
}
